/**
 * Telegram-visible character length for TipTap broadcast content.
 *
 * Telegram's `sendMessage` (4096) and `sendPhoto` caption (1024) limits are
 * defined "after entities parsing" — i.e. on the visible text the recipient
 * sees, NOT the raw HTML wire bytes. A `<b>hi</b>` wire form counts as 2 (the
 * "hi"), not 9. A `<a href="https://example.com">click</a>` counts as 5
 * (the "click" link text), not the URL bytes. A `&amp;` entity counts as 1
 * (one ampersand), not 5.
 *
 * Two helpers live here:
 *
 * - `computeTelegramVisibleLength(doc, options?)` — walks the same TipTap
 *   tree the serializer walks, mirrors its block joining + list prefixes +
 *   blockquote nesting, and counts visible characters during the walk. Use
 *   this from the composer counter (where the doc is in hand) so admins see
 *   precise numbers as they type.
 *
 * - `getTelegramVisibleLengthFromHtml(html)` — strips the serializer's
 *   tags + decodes its entity escapes to recover visible length from already
 *   serialized HTML. Use this from the worker / launch dispatch where only
 *   `Campaign.contentHtml` (post first-name substitution) is in hand. Both
 *   helpers produce identical numbers for equivalent inputs.
 *
 * Why both:
 *   - Composer / test-send know the doc → walker is exact.
 *   - Worker only has the substituted HTML at dispatch time → HTML walker
 *     decodes back to the visible length the helper would have computed
 *     from the original doc.
 */

import {
  type TipTapDocument,
  type TipTapNode,
} from './tiptap-to-telegram-html.js';

/**
 * Default token reservation for the `firstName` atom. Matches the literal
 * `{firstName}` string `tiptapToTelegramHtml` emits — the worker's
 * `substituteFirstNameIntoHtml` swaps that for the recipient's actual name at
 * dispatch. The composer counter overrides this with a worst-case 25 to keep
 * the local count pessimistic against any plausible first name.
 */
const FIRSTNAME_TOKEN_DEFAULT_LENGTH = '{firstName}'.length;

export interface ComputeTelegramVisibleLengthOptions {
  /** Override the count contribution of each `firstName` atom. */
  firstNameTokenLength?: number;
}

export function computeTelegramVisibleLength(
  doc: TipTapDocument,
  options: ComputeTelegramVisibleLengthOptions = {},
): number {
  const firstNameTokenLength =
    options.firstNameTokenLength ?? FIRSTNAME_TOKEN_DEFAULT_LENGTH;
  return countDocBlocks(doc.content, firstNameTokenLength);
}

function countDocBlocks(
  blocks: TipTapNode[] | undefined,
  firstNameTokenLength: number,
): number {
  if (!blocks || blocks.length === 0) return 0;
  // Mirror the serializer's `\n` block join EXACTLY: unsupported blocks
  // (`countBlock` returns `null`) are filtered out *before* the join — they
  // contribute no visible chars AND no join `\n` to the surrounding content.
  // Empty paragraphs (`countBlock` returns `0`) stay in the join so the
  // extra `\n` character that renders an intentional blank line is counted.
  const sums = blocks
    .map((b) => countBlock(b, firstNameTokenLength))
    .filter((n): n is number => n !== null);
  if (sums.length === 0) return 0;
  const inner = sums.reduce((a, b) => a + b, 0);
  const joinChars = sums.length > 1 ? sums.length - 1 : 0;
  return inner + joinChars;
}

function countBlock(
  node: TipTapNode,
  firstNameTokenLength: number,
): number | null {
  switch (node.type) {
    case 'paragraph':
      return countInlineChildren(node.content, firstNameTokenLength);
    case 'codeBlock':
      // `<pre>` text is plain-text only — no inline marks, no atoms.
      return countRawTextChildren(node.content);
    case 'blockquote':
      return countDocBlocks(node.content, firstNameTokenLength);
    case 'bulletList':
      return countListBlocks(node.content, null, firstNameTokenLength);
    case 'orderedList':
      return countListBlocks(node.content, 'ordered', firstNameTokenLength);
    default:
      // Unknown / unsupported block — serializer strips it ENTIRELY (no
      // join contribution either). `null` tells the caller to drop this
      // block from the join.
      return null;
  }
}

function countListBlocks(
  items: TipTapNode[] | undefined,
  kind: 'ordered' | null,
  firstNameTokenLength: number,
): number {
  if (!items || items.length === 0) return 0;
  // Match the serializer's `renderListAsPlainText`:
  //   • non-listItem items are skipped
  //   • each rendered item collapses internal `\n\n` to `\n` then prefixes
  //     `• ` (bullet) or `${index+1}. ` (ordered)
  //   • items join with `\n`
  const lines: number[] = [];
  let visibleIndex = 0;
  items.forEach((item) => {
    if (item.type !== 'listItem') return;
    visibleIndex += 1;
    const inner = countListItemInner(item.content, firstNameTokenLength);
    if (inner === 0) return; // serializer filters items whose inner is empty
    const prefix = kind === 'ordered' ? `${visibleIndex}. `.length : '• '.length;
    lines.push(prefix + inner);
  });
  if (lines.length === 0) return 0;
  return lines.reduce((a, b) => a + b, 0) + (lines.length - 1); // `\n` joins
}

/**
 * `listItem` children are ordinarily `[paragraph]`. The serializer joins
 * supported child blocks with `\n`, so multi-paragraph items contribute 1
 * newline between paragraphs. Unsupported children are filtered before the
 * join — same contract as `countDocBlocks`.
 */
function countListItemInner(
  children: TipTapNode[] | undefined,
  firstNameTokenLength: number,
): number {
  if (!children || children.length === 0) return 0;
  const sums = children
    .map((c) => countBlock(c, firstNameTokenLength))
    .filter((n): n is number => n !== null);
  if (sums.length === 0) return 0;
  const inner = sums.reduce((a, b) => a + b, 0);
  const joinChars = sums.length > 1 ? sums.length - 1 : 0;
  return inner + joinChars;
}

function countInlineChildren(
  children: TipTapNode[] | undefined,
  firstNameTokenLength: number,
): number {
  if (!children || children.length === 0) return 0;
  return children
    .map((c) => countInline(c, firstNameTokenLength))
    .reduce((a, b) => a + b, 0);
}

function countInline(node: TipTapNode, firstNameTokenLength: number): number {
  if (node.type === 'text') {
    // Telegram's "after entities parsing" length counts the raw user text,
    // NOT the entity-escaped wire form. Mark wrappers contribute zero.
    return (node.text ?? '').length;
  }
  if (node.type === 'firstName') return firstNameTokenLength;
  if (node.type === 'hardBreak') return 1;
  // Unknown / future inline node — serializer strips, length 0.
  return 0;
}

function countRawTextChildren(children: TipTapNode[] | undefined): number {
  if (!children || children.length === 0) return 0;
  return children
    .map((c) => (c.type === 'text' ? (c.text ?? '').length : 0))
    .reduce((a, b) => a + b, 0);
}

// ============================================================================
// HTML-side helper (worker / launch validation).
// ============================================================================

/**
 * Strip serializer-emitted HTML tags + decode entity escapes to recover
 * visible length from already-serialized HTML (e.g. the worker's
 * `Campaign.contentHtml` after first-name substitution).
 *
 * Safe because the serializer's contract is:
 *   - Every literal `<` / `>` / `&` in user text is escaped before output.
 *   - Tags only appear as serializer-emitted wrappers, never as user content.
 * So `<…>` only ever matches a real tag, and `&…;` only ever matches a real
 * escape. No raw-`<`-from-text edge case exists.
 */
export function getTelegramVisibleLengthFromHtml(html: string): number {
  if (!html) return 0;
  // 1) Drop tags. Greedy-match anything between `<` and `>` (no nesting in
  //    Telegram HTML mode).
  const tagless = html.replace(/<[^>]*>/g, '');
  // 2) Decode the four escapes the serializer emits (`&amp;`, `&lt;`, `&gt;`,
  //    plus `&quot;` from `escapeAttribute` — though attribute values never
  //    survive the tag strip above; included defensively).
  const decoded = tagless
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&'); // last so we don't re-decode an entity twice
  return decoded.length;
}
